import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, getToken, mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import HeroFocalPicker from "@/components/admin/HeroFocalPicker";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/admin/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!res.ok) throw new Error("Upload failed");
  return res.json();
}

const FIELDS = [
  ["brand_name", "Brand name"],
  ["brand_subtitle", "Brand subtitle"],
  ["tagline", "Hero tagline"],
  ["tagline_support", "Hero support text"],
  ["hero_type", "Hero type (image | video)"],
  ["hero_image_url", "Hero image URL"],
  ["hero_video_url", "Hero video URL"],
  ["hero_fallback_image", "Hero fallback image"],
  ["intro_text", "Introduction text (home)"],
  ["final_cta_title", "Final CTA title"],
  ["client_galleries_url", "Client galleries URL (Pixieset)"],
  ["business_email", "Business email"],
  ["whatsapp_number", "WhatsApp number"],
  ["whatsapp_message", "WhatsApp message"],
  ["location", "Location"],
  ["business_hours", "Business hours"],
  ["service_area", "Service area"],
  ["instagram_url", "Instagram URL"],
  ["tiktok_url", "TikTok URL"],
  ["about_founder_name", "About: founder name"],
  ["about_founder_role", "About: founder role"],
  ["about_founder_portrait", "About: founder portrait URL"],
  ["about_story", "About: story (multi-line)"],
  ["about_message", "About: closing message"],
  ["privacy_policy", "Privacy policy"],
  ["terms_conditions", "Terms & conditions"],
];

const TEXTAREA_FIELDS = new Set(["about_story", "privacy_policy", "terms_conditions", "intro_text", "tagline_support"]);

/** Field with quick file-upload button for image/video URL fields */
const MEDIA_FIELDS = new Set(["hero_image_url", "hero_video_url", "hero_fallback_image", "about_founder_portrait"]);

export default function AdminContent() {
  const { refresh } = useSite();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = async () => {
    const { data } = await api.get("/site/settings");
    setForm(data || {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setSaving(true);
      await api.put("/admin/site/settings", form);
      toast.success("Site content saved");
      refresh?.();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onMediaUpload = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const { url } = await uploadFile(file);
      setForm((f) => ({ ...f, [key]: url }));
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <div data-testid="admin-content">
      <p className="label-mono opacity-60">Website</p>
      <h1 className="mt-2 font-editorial text-5xl md:text-6xl">Site Content &amp; Media</h1>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
        {FIELDS.map(([key, label]) => (
          <div key={key} className={TEXTAREA_FIELDS.has(key) || key === "hero_image_url" ? "md:col-span-2" : ""}>
            <label className="label-mono opacity-60">{label}</label>
            {TEXTAREA_FIELDS.has(key) ? (
              <textarea
                rows={key === "about_story" ? 10 : 5}
                className="editorial-textarea"
                value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`content-field-${key}`}
              />
            ) : (
              <input
                className="editorial-input"
                value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                data-testid={`content-field-${key}`}
              />
            )}
            {MEDIA_FIELDS.has(key) && (
              <div className="mt-2 flex items-center gap-3">
                {form[key] && (
                  <img
                    src={mediaUrl(form[key])}
                    alt="preview"
                    className="w-20 h-16 object-cover border border-ink/15"
                  />
                )}
                <label className="label-mono opacity-70 cursor-pointer hover-underline">
                  {uploadingKey === key ? "Uploading…" : "Upload file"}
                  <input
                    type="file"
                    accept={key === "hero_video_url" ? "video/mp4" : "image/*"}
                    className="hidden"
                    onChange={(e) => onMediaUpload(key, e.target.files[0])}
                    data-testid={`content-upload-${key}`}
                  />
                </label>
              </div>
            )}
            {key === "hero_image_url" && (
              <HeroFocalPicker
                src={form.hero_image_url}
                x={form.hero_focal_x ?? 50}
                y={form.hero_focal_y ?? 50}
                onChange={(x, y) => setForm((f) => ({ ...f, hero_focal_x: x, hero_focal_y: y }))}
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-10">
        <button onClick={save} className="btn-solid" disabled={saving} data-testid="content-save">
          {saving ? "Saving…" : "Save content"}
        </button>
      </div>
    </div>
  );
}
